import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  ArrowLeft,
  Play,
  FileText,
  HelpCircle,
  CheckCircle2,
  Lock,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Award,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const QuizComponent = ({ item, onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const questions = item.questions || [];

  const handleAnswer = (idx) => {
    if (answered) return;
    setSelected(idx);
    setAnswered(true);
    if (idx === questions[current]?.answer) setScore((s) => s + 1);
  };

  const next = () => {
    if (current + 1 < questions.length) {
      setCurrent((c) => c + 1);
      setSelected(null);
      setAnswered(false);
    } else {
      setFinished(true);
      onComplete(score + (selected === questions[current]?.answer ? 0 : 0), questions.length);
    }
  };

  if (finished) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div className="text-center py-8">
        <div className="relative w-28 h-28 mx-auto mb-6">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" fill="none" stroke="var(--sand)" strokeWidth="8" />
            <circle cx="50" cy="50" r="42" fill="none" stroke={pct >= 70 ? "var(--maquis)" : "var(--terracotta)"} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${pct * 2.64} 264`} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display text-2xl font-bold">{pct}%</span>
          </div>
        </div>
        <h3 className="font-display text-2xl font-bold text-[var(--charcoal)] mb-2">
          {pct >= 70 ? "Bravo !" : "Continuez vos efforts !"}
        </h3>
        <p className="text-[var(--stone)] mb-2">
          {score} / {questions.length} bonnes reponses
        </p>
        {pct >= 70 && (
          <div className="flex items-center justify-center gap-2 text-[var(--maquis)] mt-4">
            <Award className="w-5 h-5" />
            <span className="font-medium">Quiz valide !</span>
          </div>
        )}
      </div>
    );
  }

  const q = questions[current];
  if (!q) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <span className="text-sm text-[var(--stone)]">
          Question {current + 1} / {questions.length}
        </span>
        <div className="h-2 flex-1 mx-4 bg-[var(--sand)] rounded-full overflow-hidden">
          <div
            className="h-full bg-[var(--terracotta)] rounded-full transition-all"
            style={{ width: `${((current + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>
      <h3 className="font-display text-xl font-bold text-[var(--charcoal)] mb-6">{q.q}</h3>
      <div className="space-y-3 mb-6">
        {q.options?.map((opt, idx) => {
          let cls = "quiz-option-premium";
          if (answered) {
            if (idx === q.answer) cls += " correct";
            else if (idx === selected) cls += " incorrect";
          } else if (idx === selected) {
            cls += " selected";
          }
          return (
            <button key={idx} onClick={() => handleAnswer(idx)} className={`${cls} w-full text-left`} data-testid={`quiz-option-${idx}`}>
              <span className="font-medium">{opt}</span>
            </button>
          );
        })}
      </div>
      {answered && (
        <button onClick={next} className="btn-primary w-full" data-testid="quiz-next-btn">
          {current + 1 < questions.length ? "Question suivante" : "Voir le resultat"}
        </button>
      )}
    </div>
  );
};

export default function ModuleViewer() {
  const { id } = useParams();
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [progress, setProgress] = useState(null);
  const [purchased, setPurchased] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const modRes = await axios.get(`${API}/modules/${id}`);
        setModule(modRes.data);

        if (token) {
          try {
            const purchRes = await axios.get(`${API}/modules/purchased`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const found = purchRes.data.find((m) => m.id === id);
            if (found) {
              setPurchased(true);
              setProgress(found.progress);
            }
          } catch {}
        }
      } catch {
        toast.error("Module introuvable");
      }
      setLoading(false);
    };
    load();
  }, [id, token]);

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      navigate("/auth");
      return;
    }
    setPurchasing(true);
    try {
      const res = await axios.post(
        `${API}/modules/${id}/purchase`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === "completed") {
        setPurchased(true);
        setProgress({ completed_items: [], quiz_scores: {}, progress_percent: 0 });
        toast.success("Acces accorde ! Bienvenue dans le module.");
      } else if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    }
    setPurchasing(false);
  };

  const markComplete = async (itemId) => {
    try {
      const res = await axios.post(
        `${API}/modules/${id}/progress`,
        { content_item_id: itemId, completed: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProgress((p) => ({
        ...p,
        completed_items: res.data.completed_items,
        progress_percent: res.data.progress_percent,
      }));
      if (res.data.progress_percent >= 100) {
        toast.success("Module termine ! Felicitations !");
      }
    } catch {}
  };

  const handleQuizComplete = async (itemId, score, max) => {
    try {
      const res = await axios.post(
        `${API}/modules/${id}/progress`,
        { content_item_id: itemId, completed: true, quiz_score: score, quiz_max: max },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProgress((p) => ({
        ...p,
        completed_items: res.data.completed_items,
        quiz_scores: res.data.quiz_scores,
        progress_percent: res.data.progress_percent,
      }));
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bone)] pt-32 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[var(--terracotta)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-[var(--bone)] pt-32 text-center">
        <p className="text-[var(--stone)]">Module introuvable</p>
        <Link to="/dashboard" className="text-[var(--terracotta)] mt-4 inline-block">
          Retour au centre
        </Link>
      </div>
    );
  }

  const items = (module.content_items || []).sort((a, b) => a.order - b.order);
  const completedIds = progress?.completed_items || [];
  const pct = progress?.progress_percent || 0;

  return (
    <div className="min-h-screen bg-[var(--bone)] pt-28 pb-16" data-testid="module-viewer">
      <div className="container-premium">
        {/* Back nav */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-[var(--stone)] hover:text-[var(--terracotta)] transition-colors mb-8"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au centre d'accompagnement
        </Link>

        {/* Module Header */}
        <div className="card-premium p-8 md:p-10 mb-8">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0 text-white text-3xl font-bold"
              style={{ backgroundColor: module.color || "var(--terracotta)" }}
            >
              {module.title?.charAt(0)}
            </div>
            <div className="flex-1">
              <h1 className="font-display text-3xl md:text-4xl font-bold text-[var(--charcoal)] mb-3">
                {module.title}
              </h1>
              <p className="text-[var(--stone)] leading-relaxed mb-4">{module.long_description || module.description}</p>

              <div className="flex flex-wrap items-center gap-4 text-sm text-[var(--stone)]">
                <span className="badge-premium">{items.length} contenus</span>
                {module.external_link && (
                  <a
                    href={module.external_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[var(--terracotta)] hover:underline"
                  >
                    En savoir plus <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {/* Progress bar (if purchased) */}
              {purchased && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[var(--charcoal)]">Progression</span>
                    <span className="text-sm font-bold text-[var(--terracotta)]">{pct}%</span>
                  </div>
                  <div className="h-3 bg-[var(--sand)] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: pct >= 100 ? "var(--maquis)" : "var(--terracotta)",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Access gate or content */}
        {!purchased ? (
          <div className="card-premium p-10 text-center max-w-lg mx-auto">
            <Lock className="w-12 h-12 text-[var(--stone)] mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-[var(--charcoal)] mb-3">
              Accedez a ce module
            </h2>
            <p className="text-[var(--stone)] mb-6">
              {module.price > 0
                ? `Debloquez l'acces complet pour ${module.price}EUR`
                : "Inscrivez-vous gratuitement pour acceder au contenu"}
            </p>
            <button
              onClick={handlePurchase}
              disabled={purchasing}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="purchase-btn"
            >
              {purchasing ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  {module.price > 0 ? `Acheter — ${module.price}EUR` : "Commencer gratuitement"}
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item, idx) => {
              const isCompleted = completedIds.includes(item.id);
              const isActive = activeItem === item.id;
              const Icon = item.type === "video" ? Play : item.type === "pdf" ? FileText : HelpCircle;

              return (
                <div
                  key={item.id}
                  className={`card-premium overflow-hidden transition-all ${isActive ? "ring-2 ring-[var(--terracotta)]" : ""}`}
                  data-testid={`content-item-${idx}`}
                >
                  {/* Item header */}
                  <button
                    onClick={() => setActiveItem(isActive ? null : item.id)}
                    className="w-full p-6 flex items-center gap-4 text-left"
                  >
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                        isCompleted
                          ? "bg-[var(--maquis)] text-white"
                          : "bg-[var(--sand)] text-[var(--charcoal)]"
                      }`}
                    >
                      {isCompleted ? <CheckCircle2 className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--stone)]">
                          {item.type === "video" ? "Video" : item.type === "pdf" ? "Document" : "Quiz"}
                        </span>
                        {item.duration && <span className="text-[10px] text-[var(--stone)]">• {item.duration}</span>}
                      </div>
                      <h3 className="font-semibold text-[var(--charcoal)] truncate">{item.title}</h3>
                      {item.description && <p className="text-sm text-[var(--stone)] truncate">{item.description}</p>}
                    </div>
                    {isActive ? (
                      <ChevronUp className="w-5 h-5 text-[var(--stone)]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[var(--stone)]" />
                    )}
                  </button>

                  {/* Item content */}
                  {isActive && (
                    <div className="px-6 pb-6 border-t border-[var(--sand)]">
                      <div className="pt-6">
                        {item.type === "video" && (
                          <div>
                            {item.url ? (
                              <div className="aspect-video bg-[var(--charcoal)] rounded-xl overflow-hidden mb-4">
                                <iframe
                                  src={item.url}
                                  className="w-full h-full"
                                  allowFullScreen
                                  title={item.title}
                                />
                              </div>
                            ) : (
                              <div className="aspect-video bg-[var(--sand)] rounded-xl flex items-center justify-center mb-4">
                                <div className="text-center">
                                  <Play className="w-16 h-16 text-[var(--stone)] mx-auto mb-2" />
                                  <p className="text-[var(--stone)] text-sm">Video a venir</p>
                                </div>
                              </div>
                            )}
                            {!isCompleted && (
                              <button
                                onClick={() => markComplete(item.id)}
                                className="btn-secondary text-sm w-full"
                                data-testid={`complete-${idx}`}
                              >
                                Marquer comme vu
                              </button>
                            )}
                          </div>
                        )}

                        {item.type === "pdf" && (
                          <div>
                            <div className="bg-[var(--sand)] rounded-xl p-8 text-center mb-4">
                              <FileText className="w-16 h-16 text-[var(--stone)] mx-auto mb-3" />
                              <p className="font-medium text-[var(--charcoal)] mb-2">{item.title}</p>
                              {item.url && (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-[var(--terracotta)] hover:underline"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  Ouvrir le document
                                </a>
                              )}
                            </div>
                            {!isCompleted && (
                              <button
                                onClick={() => markComplete(item.id)}
                                className="btn-secondary text-sm w-full"
                                data-testid={`complete-${idx}`}
                              >
                                Marquer comme lu
                              </button>
                            )}
                          </div>
                        )}

                        {item.type === "quiz" && (
                          <QuizComponent
                            item={item}
                            onComplete={(score, max) => handleQuizComplete(item.id, score, max)}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Completion badge */}
        {purchased && pct >= 100 && (
          <div className="card-premium p-8 mt-8 text-center bg-[var(--maquis)] text-white">
            <Award className="w-16 h-16 mx-auto mb-4 text-[var(--terracotta-light)]" />
            <h2 className="font-display text-2xl font-bold mb-2">Module termine !</h2>
            <p className="text-white/80">
              Felicitations, vous avez complete "{module.title}".
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
